/**
 * Resolve the brand theme an authenticated surface should render a form with.
 *
 * Smart Forms carry no style of their own — the design lives in a reusable,
 * org-scoped design template and the backend resolves it (explicit template ->
 * org default template -> org branding -> defaults). The public form page has
 * always received that resolved theme on its payload; nothing else did, so the
 * form instance editor, the standalone preview page and the library preview all
 * rendered unthemed. An org could author a brand and only ever see it on the
 * public link (TRUS-545 Phase 0).
 *
 * Returns `undefined` until resolved and on failure. That is deliberate:
 * `FormRenderer` gates all themed styling behind `hasTheme(theme)`, so an
 * undefined theme renders exactly as it did before this hook existed. A form is
 * never blocked, and never half-painted, by its branding.
 */

import { useEffect, useState } from 'react';

import formDesignTemplatesApi from '@/services/api/formDesignTemplates';
import type { FormTheme } from '@/components/forms/formTheme';

/**
 * Module-scoped cache, keyed by design template id ('default' for the org
 * fallback). A form library lists many templates that nearly all resolve to the
 * same org default; without this, opening each preview refetches the identical
 * theme. Cleared on org switch by `clearResolvedFormThemeCache`.
 */
const cache = new Map<string, FormTheme>();
const inflight = new Map<string, Promise<FormTheme>>();

const keyOf = (designTemplateId?: number | null) =>
  designTemplateId == null ? 'default' : String(designTemplateId);

/** Drop every cached theme. Call when the active org changes. */
export function clearResolvedFormThemeCache(): void {
  cache.clear();
  inflight.clear();
}

export function useResolvedFormTheme(designTemplateId?: number | null): FormTheme | undefined {
  const key = keyOf(designTemplateId);
  const [theme, setTheme] = useState<FormTheme | undefined>(() => cache.get(key));

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setTheme(cached);
      return;
    }

    let cancelled = false;
    setTheme(undefined);

    // Share one request per key so several surfaces mounting at once (a library
    // row and its preview, say) do not each issue their own.
    let request = inflight.get(key);
    if (!request) {
      request = formDesignTemplatesApi.resolved(designTemplateId);
      inflight.set(key, request);
      request.finally(() => inflight.delete(key));
    }

    request
      .then((resolved) => {
        cache.set(key, resolved);
        if (!cancelled) setTheme(resolved);
      })
      .catch(() => {
        // Unthemed is a correct render, not an error state — say nothing and
        // let FormRenderer fall back.
        if (!cancelled) setTheme(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [key, designTemplateId]);

  return theme;
}

export default useResolvedFormTheme;
